import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TextNode } from "lexical";
import { useEffect, useRef } from "react";

import { $createOfferNode, type OfferDefinition } from "./OfferNode";

const OFFER_TAG_PATTERN = /\{%\s*offer\s+'([^']+)'\s*%\}/g;

interface OfferAutoDetectPluginProps {
  lookup?: (slug: string) => OfferDefinition | undefined;
}

export function OfferAutoDetectPlugin({
  lookup,
}: OfferAutoDetectPluginProps = {}): null {
  const [editor] = useLexicalComposerContext();
  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;

  useEffect(() => {
    return editor.registerNodeTransform(TextNode, (node) => {
      if (!node.isSimpleText()) return;

      const text = node.getTextContent();
      const regex = new RegExp(OFFER_TAG_PATTERN.source, "g");
      const match = regex.exec(text);

      if (!match) return;

      const slug = match[1];
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      let targetNode: TextNode = node;

      if (matchStart > 0) {
        targetNode = node.splitText(matchStart)[1];
      }

      const endOffset = matchEnd - matchStart;
      if (endOffset < targetNode.getTextContentSize()) {
        targetNode.splitText(endOffset);
      }

      // Best-effort label baked at creation; OfferChip re-resolves it live from
      // the offer list at render time, so an offer that loads later still shows
      // its preview. `##########` is the same string a dynamic offer resolves
      // to, so an unresolved chip is indistinguishable from a dynamic one.
      const resolved = lookupRef.current?.(slug);
      const offerNode = $createOfferNode(
        slug,
        resolved?.preview ?? "##########",
        resolved?.fallbackCode ?? "",
      );
      targetNode.replace(offerNode);
    });
  }, [editor]);

  return null;
}
